import {
  COPY_REPEATS_ONE_DIVISION_REFUSAL,
  COPY_REPEATS_SKIP_REASONS,
  readableDate,
  type CopyRepeatsResultDto,
  type CopyRepeatsSkipReason,
  type CopyRepeatsTargetDto,
} from '@pams/shared';
import { namedOf } from '@/lib/statusPresentation';
import { DUTY_FILL_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [COPY-REPEATS] WHETHER THE COPY MAY BE CONFIRMED, AND HOW ITS ANSWER IS SAID — PURE, SO THE
 *  COMPONENT HOLDS NO RULE OF ITS OWN.
 *
 *  ⚠️ THE 80% IS ASKED HERE AND NOWHERE ELSE, OF THE FOOTER'S OWN FIGURE. The page hands in the very
 *  `named` and `total` it gives `fillFooter` — `countNamed`/`countSlots` over `slotSequence`, which
 *  already leaves out a van ([P7A §3]) and never sees a position in charge ([C6a]: those are not
 *  lines). A second derivation — the sheet's stored totals, say — goes stale after any write in the
 *  sitting (`putSlot` patches one slot), and two figures for one screen is how they come to disagree.
 *
 *  ⚠️ INTEGER CROSS-MULTIPLICATION, NEVER A FRACTION: `named × 100 ≥ total × 80`, the
 *  `dutyAnalyticsPicks.util.ts` precedent. And an OFFER gate: the server does not re-ask it, because
 *  a request that skips it reaches nothing a hand naming could not (`copyNamesToRepeats`).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const COPY_REPEATS_MIN_NAMED_PERCENT = 80;

export type CopyRepeatsOffer =
  /** Cancel only, with the sentence that says why — the button is never disabled. */
  | { kind: 'REFUSED'; sentence: string }
  /** Cancel only: `needed` more names reach the 80%. */
  | { kind: 'BELOW'; needed: number }
  | { kind: 'READY' };

export const copyRepeatsOffer = (input: {
  /** The capability's own refusal, when the caller cannot name by hand at all; else `undefined`. */
  refusal: string | undefined;
  /** How many divisions the sheet's shares span. A copy writes one division's names. */
  divisions: number;
  named: number;
  total: number;
}): CopyRepeatsOffer => {
  if (input.refusal !== undefined) return { kind: 'REFUSED', sentence: input.refusal };
  if (input.divisions > 1) return { kind: 'REFUSED', sentence: COPY_REPEATS_ONE_DIVISION_REFUSAL };
  if (input.total === 0) return { kind: 'REFUSED', sentence: DUTY_FILL_COPY.notifyNothingAsked };
  const short = input.total * COPY_REPEATS_MIN_NAMED_PERCENT - input.named * 100;
  return short <= 0 ? { kind: 'READY' } : { kind: 'BELOW', needed: Math.ceil(short / 100) };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHAT THE COPY DID, GROUPED — copied first, then one group per reason, in the server's own order.
 *
 *  ⚠️ EVERY DATE THE SERVER REACHED IS LISTED, BECAUSE A SILENT BULK WRITE ACROSS FIVE NIGHTS IS ONE
 *  NOBODY CAN CHECK. A group's count is its DATES; a row is per share, so a division that handed part
 *  of a post to a station can see one date copied for itself and skipped for the station — and the
 *  row then names whose share it is. With one share there is nothing to tell apart, and no name.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type CopyRepeatsGroup = {
  key: 'COPIED' | CopyRepeatsSkipReason;
  heading: string;
  rows: string[];
};

export const copyRepeatsGroups = (
  result: CopyRepeatsResultDto,
  /** The fill sheet's own name for an addressee — `shares[].orgUnit`. */
  nameOf: (orgUnitId: string) => string | null,
): CopyRepeatsGroup[] => {
  const several = new Set(result.targets.map((target) => target.orgUnitId)).size > 1;
  const rowOf = (target: CopyRepeatsTargetDto): string =>
    [
      readableDate(target.dutyDate) ?? target.dutyDate,
      several ? nameOf(target.orgUnitId) : null,
      target.skipped === null || target.skipped === 'MORE_THAN_HALF_NAMED'
        ? namedOf(target.namedCount, target.allocatedCount)
        : null,
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');
  const datesIn = (rows: readonly CopyRepeatsTargetDto[]): number => new Set(rows.map((row) => row.dutyDate)).size;

  const copied = result.targets.filter((target) => target.skipped === null);
  return [
    ...(copied.length > 0
      ? [
          {
            key: 'COPIED' as const,
            heading: DUTY_FILL_COPY.copyRepeatsCopied(datesIn(copied)),
            rows: copied.map(rowOf),
          },
        ]
      : []),
    ...COPY_REPEATS_SKIP_REASONS.flatMap((reason) => {
      const skipped = result.targets.filter((target) => target.skipped === reason);
      return skipped.length === 0
        ? []
        : [
            {
              key: reason,
              heading: DUTY_FILL_COPY.copyRepeatsSkipped(datesIn(skipped), reason),
              rows: skipped.map(rowOf),
            },
          ];
    }),
  ];
};

/** The answer's title: how many dates took the names, or that none did, or that there were none. */
export const copyRepeatsTitle = (result: CopyRepeatsResultDto): string => {
  const copied = new Set(result.targets.filter((target) => target.skipped === null).map((target) => target.dutyDate));
  if (copied.size > 0) return DUTY_FILL_COPY.copyRepeatsDoneTitle(copied.size);
  return result.targets.length === 0 ? DUTY_FILL_COPY.copyRepeatsNoDatesTitle : DUTY_FILL_COPY.copyRepeatsNoneTitle;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  HOW THE BUTTON LOOKS — ITS OWN COLOUR, SO IT READS AS NEITHER OF ITS NEIGHBOURS.
 *
 *  `Complete With Short-Fall` is a default button and `Send Notification` the page's one primary, and
 *  the footer's pill already spends green, amber and red on the share's fill state. So this is the
 *  `info` hue — an existing tone, no token added — under [D4.5]'s opacity-modifier rule for a wash,
 *  written exactly as `pages/Arrangements/listFiltersLook.ts` writes "Adjust filters": utility strings
 *  (Tailwind's `@layer utilities` beats `@layer antd`, so no `!important`), no `dark:` (the token is
 *  re-pointed per theme), every state's wash written out, and LITERAL class strings, because Tailwind
 *  extracts candidates from source text and an interpolated class generates nothing.
 *
 *  ⚠️ THE WORDS STAY `text-foreground`. Only the border and the icon carry the hue, where the 3:1
 *  non-text bar applies; a coloured label over its own wash is where contrast fails first.
 *
 *  ⚠️ AND IT IS NEVER DISABLED — the modal explains — so these utilities never mask AntD's disabled look.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const COPY_REPEATS_LOOK =
  'border-info bg-info/10 text-foreground hover:border-info hover:bg-info/15 hover:text-foreground active:bg-info/20';

export const COPY_REPEATS_ICON = 'size-4 text-info';
